import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { usePosts, useCreatePost, type PostRow } from "@/hooks/useBlog.js";
import { Button } from "@/components/ui/button.js";
import { RoleGate } from "@/components/RoleGate.js";

function titleIt(t: PostRow["title"]): string {
  return (t && (t.it || Object.values(t)[0])) || "";
}
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const slugify = (s: string) => s.toLowerCase().normalize("NFD").replace(new RegExp("[\\u0300-\\u036f]","g"), "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 120);
const DOT: Record<string, string> = { pubblicato: "bg-success", bozza: "bg-ink-mute", archiviato: "bg-risk" };

export default function BlogList(): JSX.Element {
  const { data, isLoading } = usePosts();
  const create = useCreatePost();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const items = data ?? [];

  async function onCreate() {
    const t = window.prompt("Titolo del nuovo articolo:");
    if (!t || !t.trim()) return;
    const slug = slugify(t) || `articolo-${items.length + 1}`;
    setCreating(true);
    try {
      const r = await create.mutateAsync({ slug, title: { it: t.trim() } });
      navigate(`/blog/${r.id}`);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-[22px]">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Blog</h1>
          <p className="mt-[5px] text-[13.5px] text-ink-soft">Articoli e contenuti del sito.</p>
        </div>
        <RoleGate min="editor">
          <Button onClick={() => void onCreate()} disabled={creating}>
            <Plus className="size-4" /> {creating ? "Creo…" : "Nuovo articolo"}
          </Button>
        </RoleGate>
      </header>

      <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-3">
        <Stat v={String(items.length)} l="Totali" />
        <Stat v={String(items.filter((p) => p.status === "pubblicato").length)} l="Pubblicati" />
        <Stat v={String(items.filter((p) => p.status === "bozza").length)} l="Bozze" last />
      </div>

      <div className="ticks relative rounded-[5px] border border-rule bg-surface">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th>Articolo</Th><Th>Stato</Th><Th>Categoria</Th>
              <Th className="text-right">Pubblicato</Th><Th className="text-right">Aggiornato</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-rule-soft transition-colors last:border-b-0 hover:bg-surface-2">
                <td className="p-[12px_14px]">
                  <Link to={`/blog/${p.id}`} className="group block">
                    <span className="block text-[13.5px] font-semibold text-ink group-hover:text-accent">{titleIt(p.title) || p.slug}</span>
                    <span className="block font-mono text-[11px] text-ink-mute">{p.slug}</span>
                  </Link>
                </td>
                <td className="p-[12px_14px]">
                  <span className="inline-flex h-[24px] items-center gap-[7px] rounded-full border border-rule bg-surface-2 px-[10px] text-[11.5px] text-ink-soft">
                    <span className={`size-[6px] rounded-full ${DOT[p.status]}`} />{p.status}
                  </span>
                </td>
                <td className="p-[12px_14px] text-[13px] text-ink-soft">{p.category || <span className="text-ink-mute">—</span>}</td>
                <td className="whitespace-nowrap p-[12px_14px] text-right font-mono text-[11.5px] text-ink-mute">{fmtDate(p.published_at)}</td>
                <td className="whitespace-nowrap p-[12px_14px] text-right font-mono text-[11.5px] text-ink-mute">{fmtDate(p.updated_at)}</td>
              </tr>
            ))}
            {!isLoading && items.length === 0 ? (
              <tr><td colSpan={5} className="p-16 text-center text-ink-mute">Nessun articolo. Creane uno.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ v, l, last }: { v: string; l: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}</div>
      <div className="micro mt-[5px]">{l}</div>
    </div>
  );
}
function Th({ children, className = "" }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`border-b border-rule p-[11px_14px] text-left font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-ink-mute ${className}`}>{children}</th>;
}
